import { supabase } from "@/lib/supabase";

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

export type IncidentCategory = "vehicle_collision" | "property_damage" | "single_vehicle" | "other";
export type FaultClassification = "not_at_fault" | "at_fault" | "section2" | "section2_with_repair_cost" | "repair_internally";
export type IncidentStatus = "draft" | "submitted_for_review" | "reviewed" | "closed";

export interface InsuranceIncident {
  id: string;
  tenant_id: string;
  reported_by: string;
  driver_name: string | null;
  vehicle_number: string | null;
  incident_category: IncidentCategory;
  incident_date: string;
  incident_time: string | null;
  location_description: string | null;
  location_lat: number | null;
  location_lng: number | null;
  weather_conditions: string | null;
  road_conditions: string | null;
  speed_limit: string | null;
  vehicle_speed: string | null;
  narrative: string | null;
  police_involved: boolean;
  police_incident_number: string | null;
  witnesses_present: boolean;
  witness_details: string | null;
  injuries: boolean;
  injury_details: string | null;
  alcohol_drugs_involved: boolean;
  vehicle_location_now: string | null;
  own_vehicle_damage_description: string | null;
  own_vehicle_existing_damage: boolean;
  /** Full Q&A transcript from the voice-guided report (if the driver used
   *  it), separate from narrative (the AI's cleaned-up summary) -- lets an
   *  admin check the driver's own words against what got filled in. */
  voice_transcript: string | null;
  fault_classification: FaultClassification | null;
  repair_cost: number | null;
  repairer_name: string | null;
  repairer_address: string | null;
  repairer_phone: string | null;
  repairer_email: string | null;
  repair_estimate: number | null;
  /** Set when responsibility is confirmed (see confirm_incident_excess); cleared if it's undone. */
  excess_applies: boolean | null;
  excess_amount: number | null;
  responsibility_confirmed_at: string | null;
  status: IncidentStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface IncidentThirdParty {
  id: string;
  incident_id: string;
  party_type: "vehicle" | "property";
  other_driver_name: string | null;
  other_driver_phone: string | null;
  other_driver_licence: string | null;
  other_vehicle_rego: string | null;
  other_vehicle_make: string | null;
  other_vehicle_model: string | null;
  other_vehicle_damage_description: string | null;
  other_owner_name: string | null;
  other_owner_phone: string | null;
  other_insurer_name: string | null;
  other_claim_policy_number: string | null;
  property_description: string | null;
  /** Body-type silhouette to draw their damage diagram on, e.g. "Sedan" --
   *  same vehicle_types catalog DMS's own fleet uses. Null until the admin
   *  picks one, at which point a diagram becomes available for this party
   *  same as it is for our own vehicle. */
  overlay_vehicle_type: string | null;
}

export interface IncidentPhoto {
  id: string;
  incident_id: string;
  category: string;
  storage_path: string;
  latitude: number | null;
  longitude: number | null;
  taken_at: string | null;
  uploaded_by: string | null;
}

export async function fetchIncidents(): Promise<InsuranceIncident[]> {
  const { data, error } = await supabase.from("insurance_incidents").select("*").order("incident_date", { ascending: false });
  if (error) {
    console.error("Error fetching incidents:", error);
    return [];
  }
  return data || [];
}

export async function fetchIncident(id: string): Promise<InsuranceIncident | null> {
  const { data, error } = await supabase.from("insurance_incidents").select("*").eq("id", id).maybeSingle();
  if (error) {
    console.error("Error fetching incident:", error);
    return null;
  }
  return data;
}

/** Most recent unsubmitted incident for a driver, if any -- lets the
 * driver-portal wizard offer to resume instead of starting over. */
export async function fetchDraftIncidentForDriver(driverName: string): Promise<InsuranceIncident | null> {
  const { data, error } = await supabase
    .from("insurance_incidents")
    .select("*")
    .eq("driver_name", driverName)
    .eq("status", "draft")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error("Error fetching draft incident:", error);
    return null;
  }
  return data;
}

export async function fetchIncidentThirdParties(incidentId: string): Promise<IncidentThirdParty[]> {
  const { data, error } = await supabase.from("insurance_incident_third_parties").select("*").eq("incident_id", incidentId);
  if (error) {
    console.error("Error fetching incident third parties:", error);
    return [];
  }
  return data || [];
}

// Damage-diagram markers -- click-to-pin damage locations on the vehicle's
// body-type silhouette (front/rear/driver/passenger, reused from the
// vehicle-inspections storage bucket), not on a real photo. Same shape as
// vehicle_inspection_damage_markers, keyed by incident+view instead of a
// photo id.
export type DamageDiagramView = "front" | "rear" | "driver" | "passenger";

export interface IncidentDamageMarker {
  id: string;
  incident_id: string;
  /** Null = our own vehicle's diagram; set = this third party's diagram. */
  third_party_id: string | null;
  view: DamageDiagramView;
  x_percent: number;
  y_percent: number;
  damage_type: string;
  severity: "Minor" | "Moderate" | "Severe";
  notes: string | null;
}

export async function fetchIncidentDamageMarkers(incidentId: string): Promise<IncidentDamageMarker[]> {
  const { data, error } = await supabase.from("insurance_incident_damage_markers").select("*").eq("incident_id", incidentId);
  if (error) {
    console.error("Error fetching incident damage markers:", error);
    return [];
  }
  return data || [];
}

/** Full replace of one view's markers -- simplest correct approach for a
 * low-frequency, few-clicks-per-incident interaction; avoids diffing.
 * thirdPartyId null scopes to our own vehicle's diagram; otherwise scopes to
 * that third party's diagram, leaving every other party's markers (and our
 * own) untouched. */
export async function replaceIncidentDamageMarkersForView(
  incidentId: string,
  view: DamageDiagramView,
  markers: Omit<IncidentDamageMarker, "id" | "incident_id" | "view" | "third_party_id">[],
  thirdPartyId: string | null = null
): Promise<void> {
  let delQuery = supabase.from("insurance_incident_damage_markers").delete().eq("incident_id", incidentId).eq("view", view);
  delQuery = thirdPartyId ? delQuery.eq("third_party_id", thirdPartyId) : delQuery.is("third_party_id", null);
  const { error: delErr } = await delQuery;
  if (delErr) throw delErr;
  if (markers.length === 0) return;
  const { error: insErr } = await supabase
    .from("insurance_incident_damage_markers")
    .insert(markers.map((m) => ({ incident_id: incidentId, view, third_party_id: thirdPartyId, ...m, created_by: "admin" })));
  if (insErr) throw insErr;
}

export async function fetchIncidentPhotos(incidentId: string): Promise<IncidentPhoto[]> {
  const { data, error } = await supabase.from("insurance_incident_photos").select("*").eq("incident_id", incidentId);
  if (error) {
    console.error("Error fetching incident photos:", error);
    return [];
  }
  return data || [];
}

export async function createIncident(
  input: Partial<Omit<InsuranceIncident, "id" | "tenant_id" | "created_at" | "updated_at">>
): Promise<InsuranceIncident> {
  const { data, error } = await supabase
    .from("insurance_incidents")
    .insert({ tenant_id: DEFAULT_TENANT_ID, ...input })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateIncident(id: string, patch: Partial<InsuranceIncident>): Promise<void> {
  const { error } = await supabase
    .from("insurance_incidents")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteIncident(id: string): Promise<void> {
  const { error } = await supabase.from("insurance_incidents").delete().eq("id", id);
  if (error) throw error;
}

/** Returns the row's id -- callers that need to keep editing the same row
 * across an autosaving multi-step form (e.g. ReportIncident.tsx) pass it
 * back in as `input.id` on the next save. */
export async function saveThirdParty(input: Partial<IncidentThirdParty> & { incident_id: string }): Promise<string> {
  if (input.id) {
    const { id, ...patch } = input;
    const { error } = await supabase.from("insurance_incident_third_parties").update(patch).eq("id", id);
    if (error) throw error;
    return id;
  }
  const { data, error } = await supabase
    .from("insurance_incident_third_parties")
    .insert({ tenant_id: DEFAULT_TENANT_ID, ...input })
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

export async function deleteThirdParty(id: string): Promise<void> {
  const { error } = await supabase.from("insurance_incident_third_parties").delete().eq("id", id);
  if (error) throw error;
}

export async function addIncidentPhoto(input: Omit<IncidentPhoto, "id">): Promise<IncidentPhoto> {
  const { data, error } = await supabase
    .from("insurance_incident_photos")
    .insert({ tenant_id: DEFAULT_TENANT_ID, ...input })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteIncidentPhoto(id: string): Promise<void> {
  const { error } = await supabase.from("insurance_incident_photos").delete().eq("id", id);
  if (error) throw error;
}

// 'third_party_at_fault' -- our driver wasn't at fault, but we still need
// our own insurer to handle it (repairs, etc.) and recover the cost from
// the at-fault third party's insurer, rather than us paying an excess.
// Distinct from 'full' (we're at fault) and 'section2' (no-fault, no cost
// to us at all -- nothing to claim).
export type ClaimType = "full" | "section2" | "third_party_at_fault";
export type ClaimStatus = "not_submitted" | "ready_to_submit" | "submitted" | "in_progress" | "settled" | "declined";

export interface InsuranceClaim {
  id: string;
  incident_id: string;
  tenant_id: string;
  claim_type: ClaimType;
  status: ClaimStatus;
  // Two distinct references -- the broker (e.g. Rothbury) issues one when
  // the claim is first sent to them, and the insurance company issues a
  // separate one once the claim is actually approved.
  broker_reference: string | null;
  claim_number: string | null;
  submitted_at: string | null;
  submitted_by: string | null;
  notes: string | null;
  created_at: string;
  // Separate from status -- Open/Closed is Janelle's own manual marker for
  // whether the claim is done and dusted (vehicle repaired, or the insurer
  // has accepted/settled it), since those two real-world events don't map
  // cleanly onto a single status value.
  is_closed: boolean;
  closed_at: string | null;
  closed_reason: string | null;
}

export async function fetchClaims(): Promise<InsuranceClaim[]> {
  const { data, error } = await supabase.from("insurance_claims").select("*").order("created_at", { ascending: false });
  if (error) {
    console.error("Error fetching claims:", error);
    return [];
  }
  return data || [];
}

export async function createClaim(input: { incident_id: string; claim_type: ClaimType; notes?: string }): Promise<InsuranceClaim> {
  const { data, error } = await supabase
    .from("insurance_claims")
    .insert({ tenant_id: DEFAULT_TENANT_ID, status: "not_submitted", ...input })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateClaim(id: string, patch: Partial<InsuranceClaim>): Promise<void> {
  const { error } = await supabase.from("insurance_claims").update(patch).eq("id", id);
  if (error) throw error;
}

/**
 * Documents the insurer sends back on a claim (acceptance letters,
 * settlement confirmations, etc.) -- separate from insurance_incident_photos
 * (scene evidence, captured against the incident). Keyed off claim_id, so
 * these apply the same whether the underlying incident was admin-logged or
 * driver self-reported. Stored in the "claim-documents" bucket at
 * `claims/{claim_id}/...`.
 */
export interface InsuranceClaimDocument {
  id: string;
  claim_id: string;
  tenant_id: string;
  label: string | null;
  storage_path: string;
  file_name: string | null;
  notes: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchClaimDocuments(claimId: string): Promise<InsuranceClaimDocument[]> {
  const { data, error } = await supabase
    .from("insurance_claim_documents")
    .select("*")
    .eq("claim_id", claimId)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("Error fetching claim documents:", error);
    return [];
  }
  return data || [];
}

export async function addClaimDocument(
  input: Omit<InsuranceClaimDocument, "id" | "tenant_id" | "created_at" | "updated_at">
): Promise<InsuranceClaimDocument> {
  const { data, error } = await supabase
    .from("insurance_claim_documents")
    .insert({ tenant_id: DEFAULT_TENANT_ID, ...input })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateClaimDocument(id: string, patch: Partial<Pick<InsuranceClaimDocument, "label" | "notes">>): Promise<void> {
  const { error } = await supabase
    .from("insurance_claim_documents")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteClaimDocument(id: string): Promise<void> {
  const { error } = await supabase.from("insurance_claim_documents").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Appends one entry to a driver's excess fund ledger, computing
 * running_balance from their last entry so history never needs
 * recomputing. `amount` is always given as a positive figure -- the sign
 * is derived from entry_type ('charge' subtracts, everything else adds).
 */
export async function recordExcessFundLedgerEntry(input: {
  driver_name: string;
  entry_type: "contribution" | "charge" | "refund" | "adjustment";
  amount: number;
  incident_id?: string;
  note?: string;
  created_by?: string;
  /** Backdates the entry (e.g. to a real bank transaction date or a
   *  historical spreadsheet row) instead of using now() -- when given,
   *  running_balance for the whole driver is recomputed in chronological
   *  order afterward, since a backdated entry can land ahead of ones
   *  already on file. */
  entryDate?: string;
  bank_reconciled?: boolean;
}): Promise<string> {
  const signedAmount = input.entry_type === "charge" ? -Math.abs(input.amount) : Math.abs(input.amount);

  if (!input.entryDate) {
    const { data: last, error: lastErr } = await supabase
      .from("driver_excess_fund_ledger")
      .select("running_balance")
      .eq("driver_name", input.driver_name)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (lastErr) throw lastErr;

    const previousBalance = last?.running_balance ?? 0;
    const runningBalance = previousBalance + signedAmount;

    const { data, error } = await supabase
      .from("driver_excess_fund_ledger")
      .insert({
        tenant_id: DEFAULT_TENANT_ID,
        driver_name: input.driver_name,
        entry_type: input.entry_type,
        amount: signedAmount,
        running_balance: runningBalance,
        incident_id: input.incident_id ?? null,
        note: input.note ?? null,
        created_by: input.created_by ?? null,
        bank_reconciled: input.bank_reconciled ?? false,
      })
      .select("id")
      .single();
    if (error) throw error;
    return data.id;
  }

  const { data, error } = await supabase
    .from("driver_excess_fund_ledger")
    .insert({
      tenant_id: DEFAULT_TENANT_ID,
      driver_name: input.driver_name,
      entry_type: input.entry_type,
      amount: signedAmount,
      running_balance: 0, // placeholder -- recomputeExcessFundRunningBalance fixes every row below
      incident_id: input.incident_id ?? null,
      note: input.note ?? null,
      created_by: input.created_by ?? null,
      created_at: new Date(input.entryDate).toISOString(),
      bank_reconciled: input.bank_reconciled ?? false,
    })
    .select("id")
    .single();
  if (error) throw error;
  await recomputeExcessFundRunningBalance(input.driver_name);
  return data.id;
}

/** Re-derives running_balance for a driver's whole ledger in chronological
 *  order -- needed after inserting a backdated entry (recordExcessFundLedgerEntry
 *  with entryDate) since it may land ahead of entries already on file. */
export async function recomputeExcessFundRunningBalance(driverName: string): Promise<void> {
  const { data: entries, error } = await supabase
    .from("driver_excess_fund_ledger")
    .select("id, amount, running_balance")
    .eq("driver_name", driverName)
    .order("created_at", { ascending: true });
  if (error) throw error;

  let balance = 0;
  for (const e of entries || []) {
    balance += e.amount;
    if (balance !== e.running_balance) {
      const { error: updateErr } = await supabase
        .from("driver_excess_fund_ledger")
        .update({ running_balance: balance })
        .eq("id", e.id);
      if (updateErr) throw updateErr;
    }
  }
}

/**
 * Creates the actual weekly-deduction row for a confirmed excess charge, so
 * it flows into WeeklyPayoutWorkspace.tsx's existing deduction aggregation
 * automatically (category 'insurance_excess_repayment' is deliberately not
 * flagged is_insurance -- see migration 0045 -- so it isn't swallowed by the
 * flat-rate 'insurance' contribution special-case, and is summed via its own
 * real amount instead). `ends_on` is computed from the payoff estimate --
 * that field is what WeeklyPayoutWorkspace actually reads to stop applying a
 * deduction once it's paid off (this app has no separate auto-decrement job;
 * remaining_balance/total_amount are tracked for visibility the same way
 * DriverEditModal's own reducing-balance deductions already work).
 */
export async function createExcessRepaymentDeduction(input: {
  driver_name: string;
  total_amount: number;
  weekly_amount: number;
  description: string;
}): Promise<void> {
  const weeksToPayoff = Math.max(1, Math.ceil(input.total_amount / input.weekly_amount));
  const endsOn = new Date();
  endsOn.setDate(endsOn.getDate() + weeksToPayoff * 7);

  const { error } = await supabase.from("driver_financial_adjustments").insert({
    tenant_id: DEFAULT_TENANT_ID,
    driver_name: input.driver_name,
    adjustment_type: "deduction",
    category: "insurance_excess_repayment",
    adjustment_mode: "reducing_balance",
    amount: input.weekly_amount,
    apply_per_payout: input.weekly_amount,
    total_amount: input.total_amount,
    remaining_balance: input.total_amount,
    active: true,
    auto_apply: true,
    priority: 100,
    starts_on: new Date().toISOString().slice(0, 10),
    ends_on: endsOn.toISOString().slice(0, 10),
    description: input.description,
  });
  if (error) throw error;
}

/**
 * Appends this week's flat-rate contribution to each driver's excess fund
 * ledger, idempotently (keyed by a note naming the pay week) so re-running
 * an export for the same week -- or using both Download Banking File and
 * Export Bank TXT for the same run -- never double-counts.
 */
export async function recordWeeklyFundContributions(
  entries: { driverName: string; amount: number }[],
  weekStartStr: string
): Promise<void> {
  const positive = entries.filter((e) => e.amount > 0);
  if (positive.length === 0) return;

  const note = `Weekly contribution -- week of ${weekStartStr}`;
  const { data: existing } = await supabase
    .from("driver_excess_fund_ledger")
    .select("driver_name")
    .eq("entry_type", "contribution")
    .eq("note", note)
    .in(
      "driver_name",
      positive.map((e) => e.driverName)
    );
  const already = new Set((existing || []).map((e) => e.driver_name));

  for (const e of positive) {
    if (already.has(e.driverName)) continue;
    await recordExcessFundLedgerEntry({ driver_name: e.driverName, entry_type: "contribution", amount: e.amount, note });
  }
}
